/**
 * @file     index.tsx
 * @purpose  Blog post page on the reading template: visible breadcrumb, the
 *           article with its sticky contents rail, one end CTA matched to the
 *           post's topic, and related posts.
 */
'use client';

import Link from 'next/link';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { hubLayoutTokens } from '@/design-system/tokens/layout';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Breadcrumb, toBreadcrumbItems } from '@/components/hub/Breadcrumb';
import { SectionHeader } from '@/components/hub/SectionHeader';
import { focusRingSx, headingFont, hubColors, hubRadius, reducedMotionSx, transition } from '@/components/hub/hubKitStyles';
import { BlogPostDetail } from '@/features/blog/components/BlogPostDetail';
import { LocalBlogCard } from '@/features/blog/components/LocalBlogCard/LocalBlogCard';
import type { ArticleEndCta } from '@/features/blog/components/articleEndCta';
import type { LocalBlogPost } from '@/features/blog/types/LocalBlogPost';
import { useBlogPostContent } from './pageContent';

type BlogPostPageProps = {
  post: LocalBlogPost;
  relatedPosts: LocalBlogPost[];
  endCta: ArticleEndCta;
};

function EndCta({ cta }: { cta: ArticleEndCta }) {
  const { trackCtaClick } = useAnalytics();
  return (
    <Box
      component="aside"
      aria-label={cta.title}
      sx={{
        mt: { xs: 2, md: 3 },
        p: { xs: 4, md: 6 },
        borderRadius: `${hubRadius.panel}px`,
        backgroundColor: hubColors.surfaceSunken,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      <Box component="p" sx={{ m: 0, fontFamily: headingFont, fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em', color: hubColors.textPrimary }}>
        {cta.title}
      </Box>
      <Box component="p" sx={{ m: 0, fontSize: 16, lineHeight: 1.55, color: hubColors.textSecondary }}>
        {cta.body}
      </Box>
      <Box
        component={Link}
        href={cta.href}
        onClick={() => trackCtaClick(cta.label, 'blog_post_crosssell', cta.href)}
        sx={{
          mt: 0.5,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 52,
          px: 3.25,
          borderRadius: `${hubRadius.control}px`,
          backgroundColor: hubColors.interactivePrimary,
          color: hubColors.textOnPrimary,
          fontSize: 16,
          fontWeight: 500,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          transition: transition('background-color'),
          '&:hover': { backgroundColor: hubColors.primaryHover },
          ...focusRingSx,
          ...reducedMotionSx,
        }}
      >
        {cta.label}
      </Box>
    </Box>
  );
}

const BlogPostPage = ({ post, relatedPosts, endCta }: BlogPostPageProps) => {
  const content = useBlogPostContent();

  return (
    <Box sx={{ color: hubColors.textPrimary, backgroundColor: hubColors.backgroundPrimary }}>
      <Container maxWidth="lg" sx={{ pt: hubLayoutTokens.gutter.fluid, pb: hubLayoutTokens.sectionPadding.fluid }}>
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Breadcrumb
            items={toBreadcrumbItems([
              { label: content.breadcrumbHome, href: '/' },
              { label: content.breadcrumbBlog, href: '/blog' },
              { label: post.title },
            ])}
          />
        </Box>

        <BlogPostDetail post={post} bylineTemplate={content.byline} footer={<EndCta cta={endCta} />} />

        {relatedPosts.length > 0 ? (
          <Box component="section" aria-labelledby="related-posts" sx={{ mt: { xs: 7, md: 10 }, display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
            <SectionHeader id="related-posts" title={content.relatedPosts.title} size="compact" />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
                gap: { xs: 4, md: 4 },
              }}
            >
              {relatedPosts.map((related) => (
                <LocalBlogCard key={related.id} post={related} locationTag="blog_post_related" />
              ))}
            </Box>
          </Box>
        ) : null}
      </Container>
    </Box>
  );
};

export default BlogPostPage;
